import Split from 'react-split'
import { classPrefix } from '../../lib/util'
import { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import gsap from 'gsap'
import { useGsapContext, useIsMobile, useRaf, useTimeoutWhen } from '../../lib/hooks'
import AudiencePane from './AudiencePane'
import { lerp } from '../../lib/math'

// HACK: react-split doesn't support a render func for the gutter, so we need to generate these DOM elements ourselves.
// react-split-grid does allow more control over the HTML structure via React but doesn't have any sensible way to figure out
// column sizes from the onDragEnd callback, which we need.
// This hook takes care of generating the gutter on demand. There is no cleanup here because they are removed when react-split unmounts.
function useCreateGutter ({ gutterRef }) {
  const createGutter = useCallback(() => {
    const gutter = document.createElement('div')
    gutter.className = classPrefix('audience-selector__gutter')

    const clickTarget = document.createElement('div')
    clickTarget.className = classPrefix('audience-selector__gutter-click-target')
    gutter.appendChild(clickTarget)

    const handle = document.createElement('div')
    handle.classList = classPrefix('audience-selector__handle')
    handle.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="66" height="66" fill="none">
        <rect width="50" height="50" x="8" y="8" fill="#fff" rx="25" />
        <path fill="#404042" d="M48.707 33.707a1 1 0 0 0 0-1.414l-6.364-6.364a1 1 0 0 0-1.414 1.414L46.586 33l-5.657 5.657a1 1 0 0 0 1.414 1.414l6.364-6.364Zm-31.414-1.414a1 1 0 0 0 0 1.414l6.364 6.364a1 1 0 0 0 1.414-1.414L19.414 33l5.657-5.657a1 1 0 0 0-1.414-1.414l-6.364 6.364ZM48 32H18v2h30v-2Z"/>
      </svg>
    `
    gutter.appendChild(handle)

    if (gutterRef != null) {
      gutterRef.current = gutter
    }

    return gutter
  }, [gutterRef])

  return createGutter
}

function TapAndDragIcon () {
  return (
    <svg width='40' height='50' viewBox='0 0 40 50' fill='none' xmlns='http://www.w3.org/2000/svg' className={classPrefix('audience-selector__tap-and-drag-icon')}>
      <g clipPath='url(#clip0_100_560)'>
        <path d='M24.3193 50.0001C23.2508 49.7393 22.1714 49.5193 21.1174 49.2104C19.2916 48.6756 17.7375 47.6287 16.3469 46.3751C14.8012 44.9809 13.6558 43.2741 12.7484 41.3967C12.169 40.1984 11.5044 39.0422 10.8758 37.8679C9.57168 35.4292 8.26519 32.9917 6.9599 30.5542C6.05005 28.8547 5.11495 27.1672 4.23634 25.452C3.70029 24.4039 4.01158 23.0265 5.21591 22.4196C6.89981 21.5722 8.59091 21.6215 10.2039 22.577C11.1474 23.1359 11.7688 24.0314 12.2772 24.9953C12.8277 26.0386 13.3806 27.0806 13.9311 28.1239C14.0909 28.428 14.2424 28.7357 14.407 29.0374C14.5717 29.3354 14.8277 29.4652 15.1618 29.3715C15.4827 29.2814 15.6005 29.0326 15.6077 28.7189C15.6113 28.5686 15.6113 28.4184 15.6113 28.2681C15.6113 21.7669 15.6246 15.2669 15.6005 8.76573C15.5957 7.38352 15.8818 6.14073 16.9202 5.1828C17.6762 4.48568 18.6186 4.22606 19.6354 4.3715C21.496 4.63712 22.7592 6.18039 22.7748 8.21044C22.7928 10.5073 22.7796 12.8042 22.7796 15.1011C22.7796 15.2657 22.7796 15.4292 22.7796 15.6612C23.8193 15.077 24.8746 14.9027 25.9659 15.3066C27.0428 15.7056 27.6907 16.541 28.127 17.5914C29.2051 16.9496 30.2424 16.4604 31.5332 17.041C32.7279 17.5782 33.4299 18.4472 33.7832 19.6083C34.3121 19.3979 34.8109 19.1239 35.3445 19.0025C37.0645 18.6071 38.8217 19.9664 39.2844 21.5758C39.3986 21.9737 39.4683 22.4003 39.4695 22.815C39.4803 26.958 39.4984 31.1023 39.4599 35.2453C39.4371 37.8066 38.7652 40.2152 37.5284 42.4652C36.7964 43.7982 35.9046 44.9977 34.8073 46.0566C33.8553 46.9761 32.8012 47.7249 31.6534 48.3835C30.5573 49.0133 29.383 49.3607 28.1738 49.6371C27.6113 49.7657 27.0452 49.8787 26.4815 50.0001H24.3181H24.3193ZM17.2039 18.5085C17.2039 21.7669 17.1895 25.0253 17.2123 28.2826C17.2207 29.4496 16.9359 30.3955 15.8049 30.964C15.3229 31.2056 14.6402 31.2189 14.163 30.964C13.5332 30.6287 13.1498 30.1311 12.8301 29.5085C12.0777 28.0446 11.3205 26.5782 10.4671 25.172C9.55726 23.6744 7.79764 23.0554 6.09452 23.8475C5.55365 24.0987 5.50197 24.3619 5.78082 24.8775C6.22072 25.6936 6.65942 26.5109 7.09572 27.3282C8.50918 29.9809 9.92264 32.6335 11.3325 35.2886C11.8229 36.2116 12.2952 37.1443 12.7916 38.0638C13.5512 39.4713 14.3049 40.8823 15.0993 42.2705C15.6738 43.2753 16.4491 44.1203 17.2784 44.934C18.4948 46.1275 19.9106 47.0253 21.49 47.5482C25.2964 48.8102 28.913 48.3883 32.246 46.0265C33.5957 45.0698 34.6883 43.8979 35.5981 42.5265C37.2087 40.0999 37.901 37.4148 37.9058 34.5326C37.913 30.7345 37.9082 26.9352 37.9058 23.1371C37.9058 22.8835 37.895 22.6251 37.8469 22.3763C37.663 21.3991 36.9226 20.7453 35.996 20.7177C34.8986 20.6852 34.05 21.4268 33.9515 22.5662C33.8962 23.208 33.9395 23.8571 33.9142 24.5013C33.8878 25.1636 33.508 25.5157 32.9659 25.416C32.5729 25.3439 32.3409 25.0025 32.3373 24.4544C32.3301 23.178 32.3565 21.9015 32.3253 20.6263C32.2892 19.1467 30.99 18.1972 29.6823 18.6564C28.9371 18.9184 28.3878 19.6708 28.3722 20.5241C28.3505 21.6948 28.3625 22.8667 28.3577 24.0374C28.3565 24.3895 28.264 24.7381 27.9094 24.8451C27.6678 24.9184 27.2916 24.9244 27.1258 24.7838C26.9214 24.6107 26.7784 24.2561 26.7736 23.9773C26.7412 22.3715 26.7688 20.7645 26.7496 19.1576C26.7448 18.7898 26.6895 18.4052 26.5705 18.059C26.3049 17.2802 25.0861 16.446 24.2075 16.8643C23.3073 17.2934 22.7664 17.9845 22.7796 19.0458C22.7988 20.6215 22.7844 22.1984 22.7844 23.7753C22.7844 24.565 22.526 24.9472 21.9912 24.9532C21.4539 24.958 21.1811 24.5686 21.1811 23.7898C21.1811 18.7152 21.1726 13.6395 21.1883 8.56501C21.1907 7.70804 21.0008 6.98929 20.3145 6.40876C19.8037 5.97606 18.6354 5.89554 18.1366 6.33544C17.4936 6.90275 17.1883 7.58905 17.1955 8.46645C17.2195 11.815 17.2051 15.1624 17.2051 18.5109L17.2039 18.5085Z' fill='white' />
        <path d='M19.9925 -1.87801e-05C20.1956 0.0444524 20.3999 0.0829139 20.6018 0.132193C23.4203 0.832914 25.2809 2.59253 26.2521 5.30046C27.1139 7.70551 26.8602 10.0445 25.7425 12.3137C25.5526 12.6995 25.1078 12.875 24.7172 12.7079C24.2785 12.5192 24.1271 12.0168 24.317 11.5228C24.573 10.8545 24.8855 10.1923 25.0189 9.49758C25.3843 7.60695 25.0189 5.85815 23.9083 4.24517C22.0033 1.48315 18.4901 1.14782 16.1884 2.60936C14.3735 3.7608 13.4348 5.5132 13.2497 7.65142C13.1355 8.96753 13.4312 10.2079 13.9937 11.3978C14.1006 11.6226 14.1163 11.9158 14.0946 12.1707C14.0634 12.5324 13.7845 12.7127 13.4456 12.7536C13.1018 12.7957 12.8194 12.6442 12.6655 12.3317C11.3494 9.65142 11.2377 6.95431 12.591 4.25118C13.3999 2.637 14.5766 1.37979 16.2725 0.687481C16.9516 0.409837 17.6691 0.225943 18.3699 -0.0012207C18.9107 -0.0012207 19.4516 -0.0012207 19.9925 -0.0012207V-1.87801e-05Z' fill='white' />
        <path d='M3.25252 9.07691C4.06141 9.96152 4.77896 10.7596 5.51574 11.5408C5.75973 11.7993 5.94964 12.0865 5.83305 12.4219C5.74892 12.6634 5.5494 12.9663 5.33666 13.0324C5.10108 13.1058 4.71166 13.0288 4.52776 12.8642C3.96286 12.3558 3.46045 11.7776 2.94122 11.2199C2.28137 10.5096 1.64074 9.78244 0.971271 9.08292C0.367905 8.45191 0.365502 7.93388 0.961655 7.29566C2.01214 6.17306 3.04579 5.03604 4.08906 3.90623C4.21166 3.77402 4.33906 3.64662 4.47247 3.52522C4.85108 3.18268 5.27175 3.16224 5.60348 3.46513C5.92199 3.75599 5.96045 4.29566 5.66718 4.64902C5.39074 4.98196 5.09747 5.30047 4.80541 5.61777C4.31021 6.15383 3.80901 6.68508 3.26093 7.27282C3.45925 7.28484 3.59747 7.29926 3.7369 7.29926C5.50973 7.30046 7.28377 7.34614 9.053 7.27883C9.81742 7.24998 10.285 8.09974 9.87752 8.71393C9.66718 9.03003 9.38473 9.0745 9.06502 9.0745C7.32223 9.07691 5.58065 9.07571 3.83786 9.07571C3.678 9.07571 3.51935 9.07571 3.25372 9.07571L3.25252 9.07691Z' fill='white' />
        <path d='M35.1692 7.23701C34.3603 6.35239 33.6427 5.55432 32.906 4.77307C32.662 4.51465 32.4721 4.22739 32.5887 3.89206C32.6728 3.65047 32.8723 3.34759 33.085 3.28148C33.3206 3.20816 33.71 3.28509 33.8939 3.44975C34.4588 3.95816 34.9613 4.53629 35.4805 5.09398C36.1403 5.80432 36.781 6.53148 37.4504 7.231C38.0538 7.86201 38.0562 8.38004 37.46 9.01826C36.4096 10.1409 35.3759 11.2779 34.3326 12.4077C34.21 12.5399 34.0826 12.6673 33.9492 12.7887C33.5706 13.1312 33.15 13.1517 32.8182 12.8488C32.4997 12.5579 32.4613 12.0183 32.7545 11.6649C33.031 11.332 33.3242 11.0135 33.6163 10.6961C34.1115 10.1601 34.6127 9.62884 35.1608 9.0411C34.9625 9.02908 34.8242 9.01465 34.6848 9.01465C32.912 9.01345 31.1379 8.96778 29.3687 9.03509C28.6043 9.06393 28.1367 8.21417 28.5442 7.59999C28.7545 7.28389 29.037 7.23941 29.3567 7.23941C31.0995 7.23701 32.8411 7.23821 34.5838 7.23821C34.7437 7.23821 34.9024 7.23821 35.168 7.23821L35.1692 7.23701Z' fill='white' />
      </g>
      <defs>
        <clipPath id='clip0_100_560'>
          <rect width='38.9688' height='50' fill='white' transform='translate(0.516632)' />
        </clipPath>
      </defs>
    </svg>

  )
}

function useScrollingEffects ({ scrollTimelineRef }) {
  const containerRef = useRef()
  const leftSplitRef = useRef()
  const rightSplitRef = useRef()
  const gutterRef = useRef()
  const [isFullyScrolled, setIsFullyScrolled] = useState(false)

  useGsapContext(() => {
    const tl = gsap.timeline({
      onUpdate () {
        setIsFullyScrolled(this.progress() >= 0.9)
      },
      onStart () {
        setIsFullyScrolled(false)
      },
      onComplete () {
        setIsFullyScrolled(true)
      }
    }).from(containerRef.current, {
      opacity: 0,
      duration: 0.5
    }, 0)
      .from(leftSplitRef.current, {
        duration: 0.5,
        translateY: '30%'
      }, 0)
      .from(rightSplitRef.current, {
        duration: 0.5,
        translateY: '10%'
      }, 0)
      .from(gutterRef.current, {
        duration: 0.5,
        opacity: 0
      }, 0)

    // FIXME: misleading not to cover both ref types here
    scrollTimelineRef(tl)
  }, [leftSplitRef, rightSplitRef, containerRef, gutterRef, scrollTimelineRef])

  return {
    containerRef,
    leftSplitRef,
    rightSplitRef,
    gutterRef,
    isFullyScrolled
  }
}

export default function AudienceSelectorModule ({ audiences, sliderPromptLabel, scrollTimelineRef }) {
  const [highlightedAudience, setHighlightedAudience] = useState(null)
  const [sizes, setSizes] = useState([50, 50])
  const [shouldShowPrompt, setShouldShowPrompt] = useState(true)
  const [dragging, setDragging] = useState(false)
  const [shouldDisablePointerEvents, setDisablePointerEvents] = useState(false)
  const splitRef = useRef()
  const { containerRef, leftSplitRef, rightSplitRef, gutterRef, isFullyScrolled } = useScrollingEffects({ scrollTimelineRef })
  const createGutter = useCreateGutter({ gutterRef })
  const [selectedAudience, setSelectedAudience] = useState(null)
  const isMobile = useIsMobile()

  const showPrompt = shouldShowPrompt && isFullyScrolled && selectedAudience == null
  const disablePointerEvents = shouldDisablePointerEvents || !isFullyScrolled

  useRaf(() => {
    // Drive the split to set positions when it's not being controlled by the user
    if (!dragging) {
      let targetSizes
      // We have a selected audience. Use a 65 / 35 split for active / inactive on desktop. On mobile the active
      // audience takes over the whole screen so the split is 100 / 0.
      if (selectedAudience != null) {
        const bigSplit = isMobile ? 100 : 65

        if (selectedAudience === 0) {
          targetSizes = [bigSplit, 100 - bigSplit]
        } else {
          targetSizes = [100 - bigSplit, bigSplit]
        }
      // When the user hovers or focusses on an unselected audience _and_ there is no selected audience,
      // we nudge the split over a bit to emphasise the audience they're hovering.
      } else if (highlightedAudience != null) {
        const bigSplit = 55
        if (highlightedAudience === 0) {
          targetSizes = [bigSplit, 100 - bigSplit]
        } else {
          targetSizes = [100 - bigSplit, bigSplit]
        }
      } else {
        // No interaction, return to the centre
        targetSizes = [50, 50]
      }

      // Smooth animate from current position to the desired size
      if (sizes[0] !== targetSizes[0] || sizes[1] !== targetSizes[1]) {
        setSizes([
          lerp(sizes[0], targetSizes[0], 0.25),
          lerp(sizes[1], targetSizes[1], 0.25)
        ])
      }
    }
  })

  // NOTE: the onDragEnd callback where this function is used does not update, so this function
  // should be assumed to be in the scope of the initial render call and you can't refer to React state.
  function handleSelectAudience (audience, pushState = true) {
    // Make sure the user is not slightly scrolled off this component
    gsap.to(window, { duration: 0.5, scrollTo: { y: containerRef.current } })
    setHighlightedAudience(null)
    setSelectedAudience(audience)

    if (pushState) {
      // Replace history states where we're just flipping between panels, otherwise push a new state
      const method = window.history.state?.audienceId != null ? 'replaceState' : 'pushState'
      window.history[method]({ audienceId: audience }, null)
    }
  }

  // Handle history states
  useEffect(() => {
    const onPopState = (event) => {
      if (event.state?.audienceId != null) {
        handleSelectAudience(event.state.audienceId, false)
      } else {
        setSelectedAudience(null)
      }
    }

    window.addEventListener('popstate', onPopState)

    return () => {
      window.removeEventListener('popstate', onPopState)
    }
    // FIXME: would need to convert handleSelectAudience to a stable callback to remove this ignore,
    // but as noted above we need to treat handleSelectAudience as having fixed scope because of Split.js.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isFullyScrolled) {
      // HACK: When this component is not fully scrolled on screen, we disable pointer events on the panels to prevent
      // triggering the hover state as it scrolls into view. If the user scrolls back up, it'll freeze the current
      // highlight state if one panel is hovered. We have to clear this value in response to scrolling up.
      setHighlightedAudience(null)
      setSelectedAudience(null)
    }
  }, [isFullyScrolled])

  // Hide the gutter after fully expanding an option on mobile so there isn't an ugly white line down the side. Preferred to put
  // this inside handleSelectAudience but it is scope captured in the Split.js callbacks so the size state is not
  // up-to-date.
  useEffect(() => {
    // Having to manually interact with the classList here since gutter is created outside of React
    const classList = gutterRef.current.classList
    const isFullyExpanded = sizes[0] >= 100 || sizes[1] >= 100
    if (selectedAudience != null && isFullyExpanded) {
      classList.add('is-selected')
    } else {
      classList.remove('is-selected')
    }
  }, [selectedAudience, sizes, gutterRef])

  // Cooldown here is to help the user orient themselves and avoid immediately activating the panel that's under the
  // mouse cursor after releasing a drag. Otherwise you get the confusing effect where you drag to select an option and,
  // after releasing, you are now accidentally hovering over the other option. The cooldown gives you time to move the mouse away first.
  useTimeoutWhen(() => {
    setDisablePointerEvents(false)
  }, 500, !dragging)

  return (
    <div
      className={classPrefix('audience-selector__container')}
      ref={containerRef}
      style={{
        pointerEvents: isFullyScrolled ? 'auto' : 'none'
      }}
    >
      <Split
        ref={splitRef}
        sizes={sizes}
        minSize={0}
        expandToMin={false}
        gutterSize={0}
        gutterAlign='center'
        snapOffset={0}
        gutter={createGutter}
        dragInterval={1}
        direction='horizontal'
        cursor='col-resize'
        className={classPrefix('audience-selector__split')}
        onDragStart={() => {
          setDragging(true)
          setDisablePointerEvents(true)
        }}
        onDrag={(newSizes) => {
          if (newSizes[0] > 55 || newSizes[1] > 55) {
            setShouldShowPrompt(false)
          }

          setSizes(newSizes)
        }}
        onDragEnd={(newSizes) => {
          if (newSizes[0] >= 60) {
            handleSelectAudience(0)
          } else if (newSizes[1] >= 60) {
            handleSelectAudience(1)
          }

          setDragging(false)
        }}
      >
        {audiences.map((audience, audienceId) => (
          <AudiencePane
            key={audienceId}
            ref={audienceId === 0 ? leftSplitRef : rightSplitRef}
            size={sizes[audienceId]}
            audienceName={audience.label}
            videoDetails={audience}
            imgSrc={`/static/benefits-audience-${audienceId}.jpg`}
            audienceId={audienceId}
            selected={selectedAudience === audienceId}
            highlighted={highlightedAudience === audienceId}
            disablePointerEvents={disablePointerEvents}
            onClick={() => {
              handleSelectAudience(audienceId)
            }}
            onActivate={() => {
              setHighlightedAudience(audienceId)
            }}
            onDeactivate={() => {
              setHighlightedAudience(null)
            }}
          />
        ))}
      </Split>
      <div
        className={classNames(
          classPrefix('audience-selector__prompt'),
          { 'is-showing': showPrompt }
        )}
        aria-hidden={!showPrompt}
      >
        <div className='d-flex flex-column gap-3 align-items-center'>
          {sliderPromptLabel}
          <TapAndDragIcon />
        </div>
      </div>
    </div>
  )
}
